import { Wallet } from "./wallet";

export class User {
    public id: number;
    public email: string;
    public name: string;
    public emailVerifiedAt: Date;
    public password: string;
    public user: UserInfo;
    public approved: boolean;
    public accessToken?: string;
    public firstName?: string;
    public lastName?: string;
    lastSelectedWorkspace: number;
}

export interface PackageTierLimits {
    maxMembers: number | null;
    maxWidgets: number | null;
    maxAgentStorageBytes: number | null;
    /** When set, plan caps live human handoffs per month; also ties handoffs to message credits (server). */
    humanInteractions?: number | null;
    enterpriseFeatureFlags?: EnterpriseFeatureFlags | null;
    agentAction?: boolean;
    interigations?: boolean;
  }
  export interface EnterpriseFeatureFlags {
    contactSales?: boolean;
    higherLimits?: boolean;
    customRoles?: boolean;
    sso?: boolean;
    whiteLabel?: boolean;
    auditLogs?: boolean;
    prioritySupport?: boolean;
    successManager?: boolean;
    slas?: boolean;
  }

export enum RoleType {
    Agency = 'Agency',
    SubAccount = 'SubAccount',
    Individual = 'Individual',
  }

export interface WorkspaceScheduleSlot {
    id?: number;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    skip: boolean;
}

export class Workspace {
    id: number;
    name: string;
    uid: string;
    ownerId: number;
    onboarding: boolean;
    packageId?: number | null;
    packageLimits?: PackageTierLimits | null;
    subscription?: Subscription;
    enableKnockioActions: boolean;
    wallet?: Wallet;
    default: boolean;
    workHours?: WorkspaceScheduleSlot[];
    workHoursTimezone?: string | null;
}

export class Subscription  {
    startDate: Date;
    endDate: Date;
    canceledAt: Date;
    /** ISO date when a scheduled downgraded plan takes effect (Polar next-period). */
    downgradeAt?: string | Date | null;
    package: Package;
    autoRenew: boolean;
    /** Present when subscription is billed through Stripe */
    stripeSubscriptionId?: string | null;
    /** Server `SubscriptionStatus` e.g. active, trialing, canceled */
    status?: string;
}

/** Lowercase values aligned with server `SubscriptionStatus` and Stripe-derived mapping. */
const TERMINAL_SUBSCRIPTION_STATUSES_FOR_UI = new Set([
    "canceled",
    "cancelled",
    "expired",
    "unpaid",
    "incomplete_expired",
    "incomplete",
    "pending",
]);

/**
 * When false, UI should not treat the subscription row as the active paid plan
 * (use `workspace.packageId` and catalog instead). Empty or missing `status` is treated as active for older session payloads.
 */
export function isSubscriptionActiveForBillingUI(sub: Subscription | null | undefined): boolean {
    if (!sub) {
        return false;
    }
    const st = String(sub.status ?? "").toLowerCase().trim();
    if (!st) {
        return true;
    }
    return !TERMINAL_SUBSCRIPTION_STATUSES_FOR_UI.has(st);
}

/** Single entry inside `packages.limits` from the API. */
export interface PackageLimitEntry {
    agentAction?: boolean;
    interigations?: boolean;
    maxMembers?: number | null;
    maxWidgets?: number | null;
    maxAgentStorageBytes?: number | null;
    humanInteractions?: number | null;
    enterpriseFeatureFlags?: Record<string, boolean>;
}

export class Package {
    id: number;
    amount: number;
    credits: number;
    /** Catalog type from API e.g. Free, Hobby, Standard, Pro, Enterprise */
    type: SubscriptionType | string;
    name: string;
    serviceCost?: number;
    overChargeRate?: number;
    stripePriceId?: string;
    stripePlanId?: string;
    billingType?: string;
    limits?: PackageLimitEntry[] | null;
    defaultKey?: boolean;
}

export enum SubscriptionType {
    Free = 'Free',
  Hobby = 'Hobby',
  Standard = 'Standard',
  Premium = 'Premium',
  Pro = 'Pro',
  Enterprise = 'Enterprise',
}

/**
 * Checkout vs in-place plan change: use Checkout when there is no active paid subscription
 * or the current package is Free; otherwise the API uses change-plan (Polar) or portal.
 */
export function shouldUseCheckoutForPlanChange(
    sub: Subscription | null | undefined,
): boolean {
    if (!isSubscriptionActiveForBillingUI(sub) || !sub?.package) {
        return true;
    }
    return sub.package.type === SubscriptionType.Free;
}

export enum BillingType {
    PAYG="PAYG",
    Subscription="Subscription"
}

export class UserInfo {
    public id: number;
    public name: string;
    public active: boolean;
    public status?: UserStatus;
    public image: string;
    public roleType: RoleType;
    public subscription: Subscription;
    public serviceCost: number;
    public workspaces: Workspace[];
}
export class RegisterDTO {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    name: string;
    subscriptionType: SubscriptionType;
}

export class VerifyTokenDTO {
    token: string;
}

export enum UserStatus {
    ACTIVE = 'active',
    BUSY = 'busy',
    OFFLINE = 'offline',
}
